import {RichtextRenderer} from '@nfq/payload-lexical-parser';
import {observer} from 'mobx-react-lite';

import {RichtextLink} from 'UI/components/layout/RichtextLink';
import {SublineRichtext} from 'UI/components/layout/SublineRichtext';
import {BlockQuote, H1, H2, H3, H4, Li, Ol, P, Ul} from 'UI/components/layout/Text';

import type {LexicalContent} from '@nfq/payload-lexical-parser';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `description` property holds the lexical rich text content that should be rendered.
     * This is typically a structured data object (e.g., from PayloadCMS) containing paragraphs, headings, and other elements.
     * It is passed to a renderer component that interprets and displays the formatted content.
     */
    description: LexicalContent;
}

/**
 * The `PayloadLexical` component is responsible for rendering rich text content provided via the `description` prop.
 * It uses the `RichtextRenderer` to interpret and display structured content such as paragraphs, lists, and list items.
 * The component also customizes the rendering of specific elements using provided React components (`Ul`, `Li`, `RichtextP`).
 * This abstraction ensures that PayloadCMS-generated lexical content is rendered consistently across the application.
 * By passing structured data to `RichtextRenderer`, it decouples content rendering from the content source, enabling flexibility.
 * It is especially useful for displaying CMS-managed descriptions in a reusable and semantic manner.
 *
 * @param props             The component props.
 * @param props.description The lexical rich text content to render, typically structured JSON from PayloadCMS.
 * @returns The rendered JSX output containing the formatted description.
 *
 * @example
 * ```tsx
 * <PayloadLexical description={myRichTextContent} />
 * ```
 */
const PayloadLexical = observer(({description}: ComponentProps) => (
    <RichtextRenderer
            // eslint-disable-next-line react/jsx-props-no-spreading
        blockRenderers={{subline: SublineRichtext}}
        content={description}
        customElements={{
            autolink: RichtextLink,
            heading: {
                h1: H1,
                h2: H2,
                h3: H3,
                h4: H4
            },
            link: RichtextLink,
            list: {
                ol: Ol,
                ul: Ul
            },
            listItem: Li,
            paragraph: P,
            quote: BlockQuote
        }}
    />
));

PayloadLexical.displayName = 'PayloadLexical';

export {PayloadLexical};